/**
 * Continuum Logging Channel API Client
 *
 * REST API client for Continuum logging channels.
 * Provides access to NATS JetStream log metadata and start sequence calculation.
 *
 * API Documentation: /Users/glen/code/accur8/godev/hermes/NEFARIO_LOGGING_API.md
 */

// ============================================================================
// Request/Response Types
// ============================================================================

export interface ContinuumChannelInfoRequest {
  processUid: string;
  channel: string;
}

export interface ContinuumChannelInfoResponse {
  streamName: string;
  firstSequence: number;
  lastSequence: number;
  messageCount: number;
  firstTimestamp: number;  // Unix timestamp in milliseconds
  lastTimestamp: number;   // Unix timestamp in milliseconds
  serviceLabel?: string;   // Service label if available
  processRun?: {
    uid: string;
    startedAt?: number;  // Process start timestamp in milliseconds
    completedAt?: number;  // Process completion timestamp in milliseconds
    // Dynamic passthrough: callers (log-viewer) read extraData/logarchive off this
    // without a typed contract.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
}

export interface ContinuumChannelStartSequenceRequest {
  processUid: string;
  channel: string;
  timeAgo?: string;        // Duration string (e.g., "5m", "1h", "30s")
  offsetFromEnd?: number;  // Number of messages from the end
}

export interface ContinuumChannelStartSequenceResponse {
  startSequence: number;
  reason: 'time' | 'offset' | 'both' | 'empty';
}

export interface ContinuumApiError {
  error: string;
}

/**
 * Thrown by the API client on a non-2xx response. Carries the HTTP status
 * so callers can distinguish "expected" outcomes (e.g. a 404 = "no log
 * stream for this process run", a normal condition for runs that produce
 * no console output) from genuine failures, without string-matching the
 * message.
 */
export class ContinuumApiHttpError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ContinuumApiHttpError";
  }
}

// ============================================================================
// API Client
// ============================================================================

export interface ContinuumApiClientOptions {
  baseUrl: string;
  authToken: string;
  /** Use cookie-based auth instead of header (default: false) */
  useCookieAuth?: boolean;
}

export class ContinuumApiClient {
  private readonly baseUrl: string;
  private readonly authToken: string;
  private readonly useCookieAuth: boolean;

  constructor(options: ContinuumApiClientOptions) {
    this.baseUrl = options.baseUrl;
    this.authToken = options.authToken;
    this.useCookieAuth = options.useCookieAuth || false;
  }

  /**
   * Get metadata about a log channel's NATS stream
   *
   * @param processUid - The process UID
   * @param channel - The channel name (e.g., "stdout", "stderr")
   * @returns Channel metadata including sequence range and timestamps
   * @throws Error if request fails (401 Unauthorized, 404 Not Found, etc.)
   */
  async getChannelInfo(
    processUid: string,
    channel: string
  ): Promise<ContinuumChannelInfoResponse> {
    const request: ContinuumChannelInfoRequest = {
      processUid,
      channel,
    };

    return this.post<ContinuumChannelInfoResponse>(
      '/api/continuum/channel_info',
      request
    );
  }

  /**
   * Calculate the optimal starting sequence for a log viewer
   *
   * Returns the sequence that satisfies BOTH time and offset constraints
   * (whichever requires going back further).
   *
   * @param processUid - The process UID
   * @param channel - The channel name
   * @param options - Time and/or offset constraints
   * @returns Start sequence and reason for the calculation
   * @throws Error if request fails
   *
   * @example
   * // Get last 5 minutes OR last 100K messages (whichever is more)
   * const result = await client.getStartSequence("proc-123", "stdout", {
   *   timeAgo: "5m",
   *   offsetFromEnd: 100000
   * });
   */
  async getStartSequence(
    processUid: string,
    channel: string,
    options: {
      timeAgo?: string;
      offsetFromEnd?: number;
    }
  ): Promise<ContinuumChannelStartSequenceResponse> {
    const request: ContinuumChannelStartSequenceRequest = {
      processUid,
      channel,
      ...options,
    };

    return this.post<ContinuumChannelStartSequenceResponse>(
      '/api/continuum/channel_start_sequence',
      request
    );
  }

  // ============================================================================
  // Private HTTP Methods
  // ============================================================================

  private async post<T>(endpoint: string, body: unknown): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add auth token as header (unless using cookie auth)
    if (!this.useCookieAuth) {
      headers['X-Auth-Token'] = this.authToken;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      credentials: this.useCookieAuth ? 'include' : 'same-origin',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` })) as ContinuumApiError;
      throw new ContinuumApiHttpError(
        response.status,
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      );
    }

    return response.json();
  }
}

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Create a Continuum API client with environment-based configuration
 *
 * @param authToken - Authentication token
 * @param baseUrl - Optional base URL (defaults to window.location.origin)
 * @returns Configured ContinuumApiClient
 */
export function createContinuumApiClient(
  authToken: string,
  baseUrl?: string
): ContinuumApiClient {
  return new ContinuumApiClient({
    baseUrl: baseUrl || window.location.origin,
    authToken,
  });
}

// ============================================================================
// Usage Examples (for documentation)
// ============================================================================

/**
 * Example: Get channel info and start sequence for tail view
 */
export async function exampleTailView(
  client: ContinuumApiClient,
  processUid: string,
  channel: string
) {
  // 1. Get channel metadata
  const info = await client.getChannelInfo(processUid, channel);
  console.log(`Stream has ${info.messageCount} messages`);
  console.log(`Range: ${info.firstSequence} to ${info.lastSequence}`);

  // 2. Calculate start position for "last 5 minutes OR last 1000 messages"
  const start = await client.getStartSequence(processUid, channel, {
    timeAgo: '5m',
    offsetFromEnd: 1000,
  });

  console.log(`Start at sequence ${start.startSequence} (reason: ${start.reason})`);

  // 3. Use start.startSequence in ContinuumSubscription
  return {
    startSequence: start.startSequence,
    channelInfo: info,
  };
}

/**
 * Example: Get channel info only
 */
export async function exampleChannelInfo(
  client: ContinuumApiClient,
  processUid: string,
  channel: string
) {
  try {
    const info = await client.getChannelInfo(processUid, channel);

    if (info.messageCount === 0) {
      console.log('No logs available yet');
      return null;
    }

    const firstTime = new Date(info.firstTimestamp);
    const lastTime = new Date(info.lastTimestamp);
    const duration = lastTime.getTime() - firstTime.getTime();

    console.log(`Channel: ${info.streamName}`);
    console.log(`Messages: ${info.messageCount}`);
    console.log(`Time range: ${firstTime.toISOString()} to ${lastTime.toISOString()}`);
    console.log(`Duration: ${Math.floor(duration / 1000 / 60)} minutes`);

    return info;
  } catch (error) {
    if (error instanceof Error) {
      if (error.message.includes('unauthorized')) {
        console.error('Authentication failed - check your auth token');
      } else if (error.message.includes('not found')) {
        console.error('Log channel does not exist');
      } else {
        console.error('Failed to get channel info:', error.message);
      }
    }
    throw error;
  }
}

/**
 * Example: Calculate start sequence with different constraints
 */
export async function exampleStartSequenceCalculations(
  client: ContinuumApiClient,
  processUid: string,
  channel: string
) {
  // Just time constraint
  const last5min = await client.getStartSequence(processUid, channel, {
    timeAgo: '5m',
  });
  console.log('Last 5 minutes:', last5min);

  // Just offset constraint
  const last1000 = await client.getStartSequence(processUid, channel, {
    offsetFromEnd: 1000,
  });
  console.log('Last 1000 messages:', last1000);

  // Both constraints (whichever gives more data)
  const both = await client.getStartSequence(processUid, channel, {
    timeAgo: '5m',
    offsetFromEnd: 1000,
  });
  console.log('Last 5min OR 1000 messages:', both);
  console.log('Reason:', both.reason); // "time", "offset", or "both"
}
